import { Link } from "react-router-dom";
import { Layers } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { Section, Loadable, Info, useLoad } from "@/components/control/kit";

const STATE = {
  bearbeitbar: ["Bearbeitbar", "border-green-200 bg-green-50 text-green-800"],
  zweite_person: ["Bearbeitbar, eigene Arbeit nur mit zweiter Person", "border-amber-200 bg-amber-50 text-amber-900"],
  nur_lesbar: ["Nur lesbar", "border-border bg-secondary text-brand-purple"],
  schutzregel: ["Schutzregel", "border-brand-purple/30 bg-white text-brand-purple"],
  fehlt: ["Fehlt (offen)", "border-red-200 bg-red-50 text-red-800"],
};
const BASIS = { konfiguration: "laut Konfiguration", code_stand: "laut Code-Stand" };

const APPLICATIONS = [
  { key: "control", title: "Control OS", audience: "Internes Team", note: "Mitglieder, Organisationen, Inhalte und Teamrechte verwalten.", route: "/", action: "Control OS öffnen" },
  { key: "members", title: "Mitglieder-App", audience: "Mitglieder", note: "Aktivitäten entdecken, Kontakte pflegen und am gemeinsamen Leben teilnehmen.", href: "https://app.visibilis.life/login", action: "Mitglieder-App öffnen" },
  { key: "organisations", title: "Organisations-Cockpit", audience: "Organisationen", note: "Angebote und Aktivitäten der eigenen Organisation bearbeiten.", href: "https://org.visibilis.life/anmelden", action: "Organisations-Cockpit öffnen" },
  { key: "website", title: "VISIBILIS-Website", audience: "Website · DEV", note: "Die bestehende Entwicklungsfassung des öffentlichen Portals ansehen.", href: "https://devvisibilis.visibilis.life/", action: "Website-Vorschau öffnen" },
];

function ApplicationLinks() {
  const linkClass = "mt-auto inline-flex min-h-[48px] items-center justify-center rounded-lg bg-brand-purple px-4 py-3 text-center text-lg font-semibold leading-snug text-white underline-offset-4 hover:underline focus-visible:outline-offset-4";
  return (
    <Section title="Anwendungen öffnen" testId="both-apps-launcher">
      <p className="mb-5 max-w-prose text-lg leading-relaxed text-muted-foreground">
        Hier erreichst du die drei bestehenden Apps und die Website-Vorschau.
        Jede Anwendung verwendet weiterhin ihre eigene Anmeldung und ihre eigenen Berechtigungen.
      </p>
      <ul className="grid gap-4 md:grid-cols-2">
        {APPLICATIONS.map((app) => (
          <li key={app.key} data-testid={`application-card-${app.key}`}
            className="flex min-w-0 flex-col gap-3 rounded-xl border border-border bg-white p-5">
            <p className="text-base font-semibold text-muted-foreground">{app.audience}</p>
            <h3 className="break-words text-2xl font-bold leading-snug text-brand-purple">{app.title}</h3>
            <p className="mb-2 text-lg leading-relaxed text-foreground">{app.note}</p>
            {app.route
              ? <Link to={app.route} className={linkClass} data-testid={`application-open-${app.key}`}>{app.action}</Link>
              : <a href={app.href} className={linkClass} data-testid={`application-open-${app.key}`}>{app.action}</a>}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function StateBadge({ state, testId }) {
  const [text, tone] = STATE[state] || [state, "border-border bg-white text-foreground"];
  return <span data-testid={testId} className={`inline-flex rounded-full border px-3 py-1 text-base font-semibold ${tone}`}>{text}</span>;
}

function AreaRow({ a }) {
  return (
    <li data-testid={`both-apps-area-${a.key}`} className="flex flex-col gap-3 py-5 xl:flex-row xl:items-start xl:justify-between">
      <div className="min-w-0">
        <p className="text-lg font-semibold leading-relaxed text-brand-purple">{a.title} <span className="text-base font-normal text-muted-foreground">· {a.app}</span></p>
        <p className="mt-1 text-lg leading-relaxed text-muted-foreground">{a.note}</p>
        {a.open.length > 0 && (
          <ul className="mt-2 list-disc pl-5 text-lg leading-relaxed" data-testid={`both-apps-open-${a.key}`}>
            {a.open.map((o) => <li key={o}>Offen: {o}</li>)}
          </ul>
        )}
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-2 xl:max-w-[45%]">
        <StateBadge state={a.state} testId={`both-apps-state-${a.key}`} />
        {a.route && <Link to={a.route} data-testid={`both-apps-link-${a.key}`}
          className="inline-flex min-h-[48px] max-w-full items-center break-words rounded-lg px-3 py-2 text-lg font-semibold text-brand-purple underline underline-offset-4">{a.title} öffnen</Link>}
      </div>
    </li>
  );
}

function Operations({ items }) {
  return (
    <Section title="Betrieb" testId="both-apps-operations">
      <p data-testid="both-apps-operations-hint" className="mb-3 text-lg leading-relaxed text-muted-foreground">
        Angaben aus Konfiguration und Code-Stand. Keine davon ist eine gemessene Verbindung oder ein bestätigter Versand;
        diese Seite prüft nichts live.
      </p>
      <Info items={items.map((o) => [o.title, `${o.state} · ${BASIS[o.basis] || o.basis} · ${o.note}`])} />
    </Section>
  );
}

export default function BothApps() {
  const st = useLoad("/admin/both-apps");
  return (
    <div className="space-y-6">
      <PageHeader title="Apps & Verwaltung" icon={Layers}
        subtitle="Drei Apps und eine Website: Zugänge und bestehende Verwaltungsbereiche im Überblick." />
      <ApplicationLinks />
      <Loadable state={st}>
        {(d) => (
          <>
            <Operations items={d.operations} />
            <Section title="Bereiche" testId="both-apps-areas">
              <ul className="divide-y divide-border">{d.areas.map((a) => <AreaRow key={a.key} a={a} />)}</ul>
            </Section>
          </>
        )}
      </Loadable>
    </div>
  );
}
